import {
  Add as AddIcon,
  BusinessCenter as BusinessIcon,
  HelpOutline as HelpIcon,
  AutoAwesome as MagicIcon,
} from '@mui/icons-material';
import {
  alpha,
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import React, { useState } from 'react';

// Import custom components
import MagicCard from '../../../components/ui/MagicCard';
import ClientLogoUpload from './ClientLogoUpload';
import { FormSection } from '../../../design-system/components/forms';
import MagicTextField from '../../../components/ui/FormFields/MagicTextField';

// Client magical elements form component with backward compatibility
const ClientMagicalElementsForm = ({
  clientData,
  data,
  onChange,
  onNestedChange,
  setFieldValue,
  errors = {},
  touched = {},
  onLogoUpload,
  onLogoRemove,
  logo,
}) => {
  const theme = useTheme();
  const [newFeature, setNewFeature] = useState('');
  
  // Support both new data prop and legacy clientData prop
  const formData = data || clientData || {};
  
  // Get magical elements data or initialize with defaults
  const magicalElements = formData.magicalElements || {
    transformationNarrative: '',
    enchantmentLevel: 'moderate',
    specialFeatures: [],
  };
  
  // Handle text change in transformation narrative with backward compatibility
  const handleNarrativeChange = (e) => {
    const value = e.target.value;
    
    // Support legacy nested change handler
    if (onNestedChange) {
      onNestedChange('magicalElements', 'transformationNarrative', value);
    } 
    // Support new formik-style change handler
    else if (setFieldValue) {
      setFieldValue('magicalElements.transformationNarrative', value);
    }
    // Support direct onChange handler (using name, value pattern)
    else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange('magicalElements.transformationNarrative', value);
    }
    // Support direct onChange handler (event-based)
    else if (onChange && typeof onChange === 'function' && onChange.length !== 2) {
      onChange(e);
    }
  };
  
  // Handle radio change for enchantment level with backward compatibility
  const handleEnchantmentLevelChange = (e) => {
    const value = e.target.value;
    
    // Support multiple API patterns
    if (onNestedChange) {
      onNestedChange('magicalElements', 'enchantmentLevel', value);
    } else if (setFieldValue) {
      setFieldValue('magicalElements.enchantmentLevel', value);
    } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange('magicalElements.enchantmentLevel', value);
    } else if (onChange && typeof onChange === 'function' && onChange.length !== 2) {
      onChange(e);
    }
  };
  
  // Handle adding a new special feature with backward compatibility
  const handleAddFeature = () => {
    if (newFeature.trim() && !magicalElements?.specialFeatures?.includes(newFeature.trim())) {
      const updatedFeatures = magicalElements?.specialFeatures ? [...magicalElements?.specialFeatures, newFeature.trim()] : [newFeature.trim()];
      
      // Support multiple API patterns
      if (onNestedChange) {
        onNestedChange('magicalElements', 'specialFeatures', updatedFeatures);
      } else if (setFieldValue) {
        setFieldValue('magicalElements.specialFeatures', updatedFeatures);
      } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
        onChange('magicalElements.specialFeatures', updatedFeatures);
      }
      
      setNewFeature('');
    }
  };
  
  // Handle deleting a special feature with backward compatibility
  const handleDeleteFeature = (featureToDelete) => {
    const updatedFeatures = magicalElements.specialFeatures.filter(
      feature => feature !== featureToDelete
    );
    
    // Support multiple API patterns
    if (onNestedChange) {
      onNestedChange('magicalElements', 'specialFeatures', updatedFeatures);
    } else if (setFieldValue) {
      setFieldValue('magicalElements.specialFeatures', updatedFeatures);
    } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange('magicalElements.specialFeatures', updatedFeatures);
    }
  };
  
  // Handle new feature input keydown
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFeature();
    }
  };
  
  // Special feature suggestions
  const featureSuggestions = [
    'Interactive Social Posts',
    'AR Filters',
    'Personalized Content',
    'Storytelling Elements',
    'Animated Content',
    'Visual Transformations',
    'Character Development',
    'Emotional Connection',
    'Surprise Elements',
    'Brand Mythology',
    'Visual Enchantments',
    'Digital Easter Eggs',
  ];
  
  return (
    <Grid container spacing={4}>
      {/* Client Logo Section */}
      <Grid item xs={12}>
        <FormSection
          icon={<BusinessIcon />}
          title="Client Image"
        >
          <ClientLogoUpload
            logo={logo}
            clientName={formData.name}
            onUpload={onLogoUpload}
            onRemove={onLogoRemove}
          />
        </FormSection>
      </Grid>
      
      {/* Magical Elements Section */}
      <Grid item xs={12}>
        <FormSection
          icon={<MagicIcon />}
          title="Magical Elements"
          tooltip="Enhance your client's marketing with magical elements that create emotional connections and memorable experiences"
        >
          <MagicCard 
            magical={true} 
            level={2}
            sx={{ mb: 3 }}
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Transformation Narrative
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                A brief story about how this brand transforms its customers' lives or experiences. 
                This narrative will guide content creation and campaign themes.
              </Typography>
              <MagicTextField
                fullWidth
                multiline
                rows={4}
                placeholder="Once upon a time..."
                value={magicalElements.transformationNarrative}
                onChange={handleNarrativeChange}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.paper, 0.7),
                  }
                }}
              />
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Enchantment Level
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Determines how prominently magical elements appear in marketing content.
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={magicalElements.enchantmentLevel}
                    onChange={handleEnchantmentLevelChange}
                  >
                    <FormControlLabel 
                      value="subtle" 
                      control={<Radio />} 
                      label="Subtle" 
                    />
                    <FormControlLabel 
                      value="moderate" 
                      control={<Radio />} 
                      label="Moderate" 
                    />
                    <FormControlLabel 
                      value="prominent" 
                      control={<Radio />} 
                      label="Prominent" 
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Special Features
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Unique magical elements to incorporate into this client's marketing.
                </Typography>
                
                <Autocomplete
                  freeSolo
                  options={featureSuggestions.filter(
                    option => !magicalElements?.specialFeatures?.includes(option)
                  )}
                  inputValue={newFeature}
                  onInputChange={(event, newValue) => setNewFeature(newValue)}
                  renderInput={(params) => (
                    <MagicTextField 
                      {...params} 
                      label="Add Special Feature"
                      onKeyDown={handleKeyDown}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            <InputAdornment position="end">
                              <IconButton 
                                onClick={handleAddFeature}
                                edge="end"
                                disabled={!newFeature.trim()}
                              >
                                <AddIcon />
                              </IconButton>
                            </InputAdornment>
                          </>
                        ),
                      }}
                    />
                  )}
                />
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {magicalElements?.specialFeatures?.map((feature) => (
                    <Chip
                      key={feature}
                      label={feature}
                      onDelete={() => handleDeleteFeature(feature)}
                      variant="outlined"
                      sx={{ m: 0.5 }}
                    />
                  ))}
                  {magicalElements?.specialFeatures?.length === 0 && (
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      No special features added yet
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </MagicCard>
          
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              bgcolor: alpha(theme.palette.info.light, 0.1),
              borderLeft: `4px solid ${theme.palette.info.main}`,
            }}
          >
            <Typography variant="body2" color="textSecondary">
              <strong>Tip:</strong> Magical elements help differentiate your client's brand and create
              memorable experiences for their audience. These elements guide content creation and campaign
              themes to evoke emotion and enhance engagement.
            </Typography>
          </Paper>
        </FormSection>
      </Grid>
    </Grid>
  );
};

export default ClientMagicalElementsForm;